import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TableFooter,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Alert
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { getCategoryOptions } from '../constants/categories';

interface Item {
  id: number;
  name: string;
  quantityUnit: string;
  pricePerUnit: number | null;
  addAmountToBillInPOS: boolean;
  manufacturedInFactory: boolean;
  category: string;
  consumable: number;
  manufacturingPricePerUnit: number | null;
  avgWeightPerUnit: number | null;
}

interface BillItem {
  itemName: string;
  quantity: number;
  pricePerUnit: number;
  quantityUnit: string;
  total: number;
  discount: number;
}

interface CardDiscount {
  id: number;
  bankName: string;
  cardStartingDigits: string;
  minimumSpend: number;
  maximumDiscountCap: number;
  maximumDailyUses: number;
  maximumMonthlyUses: number;
  discountPercentage: number;
}

interface SelectedItem {
  name: string;
  pricePerUnit: number;
  quantityUnit: string;
  category: string;
  addAmountToBillInPOS: boolean;
  manufacturingPricePerUnit: number | null;
  avgWeightPerUnit: number | null;
}

const POS: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [items, setItems] = useState<Item[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [pieceCount, setPieceCount] = useState(1);
  const [currentBill, setCurrentBill] = useState<BillItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<SelectedItem>({
    name: '',
    pricePerUnit: 0,
    quantityUnit: '',
    category: '',
    addAmountToBillInPOS: false,
    manufacturingPricePerUnit: null,
    avgWeightPerUnit: null
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [orderType, setOrderType] = useState('');
  const [deliveryCharges, setDeliveryCharges] = useState<number>(0);
  const [applyGstOnDelivery, setApplyGstOnDelivery] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardDiscounts, setCardDiscounts] = useState<CardDiscount[]>([]);
  const [selectedCardDiscountId, setSelectedCardDiscountId] = useState<number | null>(null);
  const [customerCardNumber, setCustomerCardNumber] = useState('');
  const [checkoutError, setCheckoutError] = useState('');

  const paymentMethodOptions = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CARD', label: 'Card' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' }
  ];

  const orderTypeOptions = [
    { value: 'WALKIN', label: 'Walkin Customer' },
    { value: 'WEBSITE', label: 'Website' },
    { value: 'PHONE', label: 'Phone Order' },
    { value: 'FOODPANDA', label: 'Food Panda' }
  ];

  useEffect(() => {
    if (token) {
      fetchShopItems();
      fetchCardDiscounts();
    }
  }, [token]);

  const fetchShopItems = async () => {
    const shopName = localStorage.getItem('shopName');
    
    try {
      let response;
      if (shopName) {
        // If shop name is set, use the shop-specific endpoint
        response = await axios.get(`${API_BASE_URL}/inventory-transactions/status/shop/${encodeURIComponent(shopName)}`, {
          headers: { Authorization: token }
        });
      } else {
        // If no shop name is set, use the general status endpoint
        response = await axios.get(`${API_BASE_URL}/inventory-transactions/status`, {
          headers: { Authorization: token }
        });
      }
      
      // Transform the response data to match the Item interface
      const transformedItems = response.data.map((item: any) => ({
        id: item.id || 0,
        name: item.itemName,
        quantityUnit: item.quantityUnit,
        pricePerUnit: item.pricePerUnit || 0,
        addAmountToBillInPOS: item.addAmountToBillInPOS ?? true,
        manufacturedInFactory: item.manufacturedInFactory ?? false,
        category: item.category,
        consumable: item.consumable || 0,
        manufacturingPricePerUnit: item.manufacturingPricePerUnit || 0,
        avgWeightPerUnit: item.avgWeightPerUnit || 0
      }));
      setItems(transformedItems);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('Permission denied:', err.response?.data);
      } else {
        console.error('Error fetching items:', err);
      }
    }
  };

  const fetchCardDiscounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/card-discounts/enabled`, {
        headers: { Authorization: token }
      });
      setCardDiscounts(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching card discounts:', err);
      }
    }
  };

  const handleAddToBill = () => {
    if (!selectedItem.name || quantity <= 0) {
      return;
    }

    const itemDetails = items.find(item => item.name === selectedItem.name);
    if (!itemDetails) {
      return;
    }

    // Check if the requested quantity exceeds the consumable limit
    if (quantity > itemDetails.consumable) {
      return;
    }

    // Check if item already exists in the bill
    const existingItemIndex = currentBill.findIndex(item => item.itemName === selectedItem.name);
    
    if (existingItemIndex >= 0) {
      // Update existing item's quantity and total
      const updatedBill = [...currentBill];
      const existingItem = updatedBill[existingItemIndex];
      const newQuantity = existingItem.quantity + quantity;
      
      // Check if the new total quantity exceeds the consumable limit
      if (newQuantity > itemDetails.consumable) {
        return;
      }
      
      const pricePerUnit = itemDetails.addAmountToBillInPOS ? (itemDetails.pricePerUnit ?? 0) : 0;
      const newTotal = pricePerUnit * newQuantity * (1 - existingItem.discount / 100);
      
      updatedBill[existingItemIndex] = {
        ...existingItem,
        quantity: newQuantity,
        pricePerUnit: pricePerUnit,
        total: newTotal
      };
      
      setCurrentBill(updatedBill);
    } else {
      // Add new item to bill
      const pricePerUnit = itemDetails.addAmountToBillInPOS ? (itemDetails.pricePerUnit ?? 0) : 0;
      const total = pricePerUnit * quantity;
      const newBillItem: BillItem = {
        itemName: selectedItem.name,
        quantity,
        pricePerUnit: pricePerUnit,
        quantityUnit: selectedItem.quantityUnit,
        total,
        discount: 0
      };

      setCurrentBill([...currentBill, newBillItem]);
    }

    // Reset selection
    setSelectedItem({ name: '', pricePerUnit: 0, quantityUnit: '', category: '', addAmountToBillInPOS: false, manufacturingPricePerUnit: null, avgWeightPerUnit: null });
    setQuantity(1);
  };

  const handleRemoveFromBill = (index: number) => {
    const updatedBill = [...currentBill];
    updatedBill.splice(index, 1);
    setCurrentBill(updatedBill);
  };

  const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

  const calculateTotal = () => roundMoney(
    currentBill.reduce((sum, item) => sum + item.total, 0)
  );

  const selectedCardDiscount = cardDiscounts.find(card => card.id === selectedCardDiscountId) || null;
  const hasItemDiscount = currentBill.some(item => item.discount > 0);
  const hasDiscountConflict = paymentMethod === 'CARD' && Boolean(selectedCardDiscount) && hasItemDiscount;

  const handleItemDiscountChange = (index: number, newDiscount: number) => {
    const updatedBill = [...currentBill];
    const item = updatedBill[index];
    const clampedDiscount = Math.min(Math.max(newDiscount, 0), 10);
    item.discount = clampedDiscount;
    item.total = item.pricePerUnit * item.quantity * (1 - clampedDiscount / 100);
    setCurrentBill(updatedBill);
    setCheckoutError('');
  };

  const calculateCardDiscount = () => {
    if (paymentMethod !== 'CARD' || !selectedCardDiscount || hasItemDiscount) return 0;
    const subtotal = calculateTotal();
    if (subtotal < Number(selectedCardDiscount.minimumSpend)) return 0;
    return roundMoney(Math.min(
      subtotal * Number(selectedCardDiscount.discountPercentage) / 100,
      Number(selectedCardDiscount.maximumDiscountCap),
      subtotal
    ));
  };

  const calculateGst = () => {
    const discountedSubtotal = calculateTotal() - calculateCardDiscount();
    return roundMoney((discountedSubtotal + (applyGstOnDelivery ? deliveryCharges : 0)) * 0.18);
  };

  const handleGenerateBill = async () => {
    if (currentBill.length === 0) {
      return;
    }

    if (!paymentMethod || !orderType) {
      return;
    }
    if (paymentMethod === 'CARD' && selectedCardDiscountId && !/^\d{12,19}$/.test(customerCardNumber)) {
      setCheckoutError('Enter a valid 12 to 19 digit customer card number for the selected discount');
      return;
    }
    if (hasDiscountConflict) {
      setCheckoutError(
        'Compounding discounts is not allowed. Remove either the card discount or all item discounts'
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setCheckoutError('');
      
      // Convert current bill items to the format expected by the backend
      const billItems = currentBill.map(item => ({
        itemName: item.itemName,
        quantity: item.quantity,
        pricePerUnit: item.pricePerUnit,
        discount: item.discount
      }));

      const response = await fetch(`${API_BASE_URL}/bills/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token') || ''
        },
        body: JSON.stringify({
          items: billItems,
          paymentMethod,
          orderType,
          deliveryCharges,
          applyGstOnDelivery,
          cardDiscountId: paymentMethod === 'CARD' ? selectedCardDiscountId : null,
          customerCardNumber: paymentMethod === 'CARD' ? customerCardNumber : null
        })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to generate bill';
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } else {
          errorMessage = await response.text();
        }
        
        throw new Error(errorMessage);
      }

      const billData = await response.json();
      
      // Clear the current bill and selections
      setCurrentBill([]);
      setPaymentMethod('');
      setOrderType('');
      setDeliveryCharges(0);
      setApplyGstOnDelivery(false);
      setSelectedCardDiscountId(null);
      setCustomerCardNumber('');
      setCheckoutError('');
      
      // Refresh the shop items data to update quantities
      await fetchShopItems();
      
      // Reset the form
      setSelectedItem({
        name: '',
        pricePerUnit: 0,
        quantityUnit: '',
        category: '',
        addAmountToBillInPOS: false,
        manufacturingPricePerUnit: null,
        avgWeightPerUnit: null
      });
      setQuantity(1);
    } catch (error) {
      console.error('Error generating bill:', error);
      setCheckoutError(error instanceof Error ? error.message : 'Failed to generate bill');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {/* Left side - Item Selection */}
      <Box sx={{ flex: 1 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Add Items</Typography>
          {/* Category selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedItem.category || ''}
              onChange={(e) => {
                const category = e.target.value;
                setSelectedItem({
                  name: '',
                  pricePerUnit: 0,
                  quantityUnit: '',
                  category: category,
                  addAmountToBillInPOS: false,
                  manufacturingPricePerUnit: null,
                  avgWeightPerUnit: null
                });
              }}
              label="Category"
            >
              {getCategoryOptions().map((category) => {
                const hasItems = items.some(i => i.category === category.value);
                return (
                  <MenuItem 
                    key={category.value} 
                    value={category.value}
                    sx={{
                      color: hasItems ? 'inherit' : 'error.main'
                    }}
                  >
                    {category.label}
                    {hasItems ? '' : ' - Out of Stock'}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          {/* Item selection */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Item</InputLabel>
              <Select
                value={selectedItem.name}
                onChange={(e) => {
                  const selectedItemName = e.target.value;
                  const item = items.find(i => i.name === selectedItemName);
                  if (item) {
                    setSelectedItem({
                      name: item.name,
                      pricePerUnit: item.pricePerUnit || 0,
                      quantityUnit: item.quantityUnit,
                      category: item.category,
                      addAmountToBillInPOS: item.addAmountToBillInPOS,
                      manufacturingPricePerUnit: item.manufacturingPricePerUnit,
                      avgWeightPerUnit: item.avgWeightPerUnit || 0
                    });
                    setQuantity(1);
                    setPieceCount(1);
                  }
                }}
                label="Item"
                disabled={!selectedItem.category}
              >
                {items
                  .filter(item => item.category === selectedItem.category)
                  .map((item) => {
                    const isDisabled = item.consumable <= 0;
                    return (
                      <MenuItem 
                        key={item.id} 
                        value={item.name}
                        disabled={isDisabled}
                        sx={{
                          color: isDisabled ? 'error.main' : 'inherit'
                        }}
                      >
                        {item.name} (PKR{item.pricePerUnit?.toFixed(2) || 'N/A'}/{item.quantityUnit})
                        {isDisabled && ' - Out of Stock'}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
            {selectedItem.quantityUnit === 'GRAM' && (
              <TextField
                label="Piece Count"
                type="number"
                value={pieceCount}
                onChange={(e) => {
                  const newPieceCount = parseInt(e.target.value) || 0;
                  setPieceCount(newPieceCount);
                  if (selectedItem.avgWeightPerUnit) {
                    const calculatedQuantity = newPieceCount * selectedItem.avgWeightPerUnit;
                    setQuantity(Math.min(calculatedQuantity, items.find(i => i.name === selectedItem.name)?.consumable || 0));
                  }
                }}
                InputProps={{
                  inputProps: { min: 1 }
                }}
                sx={{ width: 150 }}
              />
            )}
            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => {
                const newQuantity = parseInt(e.target.value) || 0;
                const selectedItemDetails = items.find(i => i.name === selectedItem.name);
                if (selectedItemDetails) {
                  const maxQuantity = selectedItemDetails.consumable;
                  const finalQuantity = Math.min(Math.max(newQuantity, 1), maxQuantity);
                  setQuantity(finalQuantity);
                  
                  // For GRAM items, also update piece count based on quantity
                  if (selectedItem.quantityUnit === 'GRAM' && selectedItem.avgWeightPerUnit && selectedItem.avgWeightPerUnit > 0) {
                    const calculatedPieceCount = Math.round(finalQuantity / selectedItem.avgWeightPerUnit);
                    setPieceCount(Math.max(calculatedPieceCount, 1));
                  }
                } else {
                  setQuantity(newQuantity);
                }
              }}
              InputProps={{
                inputProps: { 
                  min: 1,
                  max: items.find(i => i.name === selectedItem.name)?.consumable || 1
                }
              }}
              sx={{ width: 150 }}
            />
            <Button 
              variant="contained" 
              onClick={handleAddToBill}
              disabled={!selectedItem.name || quantity <= 0}
            >
              Add to Bill
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Right side - Bill Preview */}
      <Box sx={{ flex: 1 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Current Bill</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price/Unit</TableCell>
                  <TableCell align="right">Discount (%)</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentBill.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.itemName}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">PKR{item.pricePerUnit.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={item.discount}
                        onChange={(event) =>
                          handleItemDiscountChange(index, parseFloat(event.target.value) || 0)
                        }
                        InputProps={{ inputProps: { min: 0, max: 10, step: 1 } }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="right">PKR{item.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveFromBill(index)}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Typography variant="subtitle1" fontWeight="bold">
                      Subtotal:
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" fontWeight="bold">
                      PKR{calculateTotal().toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        label="Delivery Charges"
                        type="number"
                        value={deliveryCharges}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setDeliveryCharges(Math.max(value, 0));
                        }}
                        InputProps={{
                          inputProps: { min: 0, step: 0.01 }
                        }}
                        sx={{ width: 150 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={applyGstOnDelivery}
                            onChange={(e) => setApplyGstOnDelivery(e.target.checked)}
                          />
                        }
                        label="Apply GST on Delivery"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1">
                      PKR{deliveryCharges.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
                {paymentMethod === 'CARD' && selectedCardDiscount && (
                  <TableRow>
                    <TableCell colSpan={4} align="right">
                      <Typography variant="subtitle1">Card Discount:</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" color="success.main">
                        - PKR{calculateCardDiscount().toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Typography variant="subtitle1">
                      GST (18%):
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1">
                      PKR{calculateGst().toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total:
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" fontWeight="bold">
                      PKR{(
                        calculateTotal() + 
                        deliveryCharges + 
                        calculateGst() -
                        calculateCardDiscount()
                      ).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          {/* Payment and Order Details */}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                value={paymentMethod ? paymentMethodOptions.find(option => option.value === paymentMethod) : null}
                onChange={(event, newValue) => {
                  const nextMethod = newValue?.value || '';
                  setPaymentMethod(nextMethod);
                  setCheckoutError('');
                  if (nextMethod !== 'CARD') {
                    setSelectedCardDiscountId(null);
                    setCustomerCardNumber('');
                  }
                }}
                options={paymentMethodOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payment Method"
                    required
                  />
                )}
                fullWidth
              />
              <Autocomplete
                value={orderType ? orderTypeOptions.find(option => option.value === orderType) : null}
                onChange={(event, newValue) => {
                  setOrderType(newValue?.value || '');
                }}
                options={orderTypeOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Order Type"
                    required
                  />
                )}
                fullWidth
              />
            </Box>
            {paymentMethod === 'CARD' && (
              <>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                  <Autocomplete
                    value={selectedCardDiscount}
                    onChange={(_, newValue) => {
                      setSelectedCardDiscountId(newValue?.id || null);
                      if (!newValue) {
                        setCustomerCardNumber('');
                      }
                      setCheckoutError('');
                    }}
                    options={cardDiscounts}
                    getOptionLabel={(option) =>
                      `${option.bankName} - ${option.cardStartingDigits} - ${Number(option.discountPercentage)}%`
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Discount Card (Optional)" />
                    )}
                    fullWidth
                  />
                  {selectedCardDiscount && (
                    <TextField
                      label="Customer Card Number"
                      type="password"
                      value={customerCardNumber}
                      onChange={(event) => {
                        setCustomerCardNumber(event.target.value.replace(/\D/g, '').slice(0, 19));
                        setCheckoutError('');
                      }}
                      inputProps={{ inputMode: 'numeric', minLength: 12, maxLength: 19 }}
                      autoComplete="off"
                      required
                      fullWidth
                      helperText="Used securely to enforce daily and monthly limits; the number is not stored"
                    />
                  )}
                </Box>
                {selectedCardDiscount && (
                  <Typography variant="body2" color={
                    calculateTotal() >= Number(selectedCardDiscount.minimumSpend) ? 'text.secondary' : 'error'
                  }>
                    Minimum spend PKR {Number(selectedCardDiscount.minimumSpend).toFixed(2)} ·
                    Maximum discount PKR {Number(selectedCardDiscount.maximumDiscountCap).toFixed(2)} ·
                    Daily limit {selectedCardDiscount.maximumDailyUses} · Monthly limit {selectedCardDiscount.maximumMonthlyUses}
                  </Typography>
                )}
              </>
            )}
            {hasDiscountConflict && (
              <Alert severity="error">
                Compounding discounts is not allowed. Remove either the card discount or all item discounts.
              </Alert>
            )}
            {checkoutError && <Alert severity="error">{checkoutError}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateBill}
                disabled={
                  currentBill.length === 0 ||
                  !paymentMethod ||
                  !orderType ||
                  isSubmitting ||
                  hasDiscountConflict ||
                  (paymentMethod === 'CARD' && Boolean(selectedCardDiscountId) && customerCardNumber.length < 12)
                }
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Generate Bill'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default POS;
